import React, { useEffect, useRef, useState } from 'react';

import type { IntlShape } from 'react-intl';
import Loadable from 'react-loadable';
// oxlint-disable-next-line @atlassian/no-restricted-imports
import { lazy, LazySuspense } from 'react-loosely-lazy';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import type {
	ExtensionAPI,
	ExtensionManifest,
	ExtensionProvider,
	ExtensionToolbarButton,
} from '../extensions';
import { getContextualToolbarItemsFromModule } from '../extensions';
import type { DropdownOptionT } from '../types';
import { nodeToJSON } from '../utils';
import { DropdownMenuItem } from './DropdownMenuItem';
import { DropdownSeparator } from './DropdownSeparator';

export interface DropdownMenuOptions {
	dispatchCommand: Function;
	hide: Function;
	intl: IntlShape;
	showSelected: boolean;
}

type OverflowExtensionPlaceholderProps = {
	areAnyNewToolbarFlagsEnabled: boolean;
	disabled?: (key: string) => boolean;
	dropdownOptions?: DropdownMenuOptions;
	editorView: EditorView;
	extension: ExtensionProps;
	node: PMNode;
};

export type ExtensionProps = {
	extensionApi?: ExtensionAPI;
	extensionProvider?: Promise<ExtensionProvider>;
};
type ExtensionIconModule = ExtensionToolbarButton['icon'];

const noop = () => null;

const isDefaultExport = <T extends object>(mod: T | { default: T }): mod is { default: T } => {
	return mod.hasOwnProperty('default');
};

const resolveExtensionIcon = async (getIcon: ExtensionIconModule) => {
	if (!getIcon) {
		return noop;
	}
	const maybeIcon = await getIcon();
	return isDefaultExport(maybeIcon) ? maybeIcon.default : maybeIcon;
};

const convertExtensionToDropdownMenuItem = ({
	item,
	disabled,
	node,
	extension,
	IconComponent,
}: {
	disabled?: (key: string) => boolean;
	extension: ExtensionProps;
	IconComponent: React.ComponentType<{ label: string }> | null;
	item: ExtensionToolbarButton;
	node: PMNode;
}): DropdownOptionT<Function> => {
	let title = '';
	if (item.label) {
		title = item.label;
	} else if (typeof item.tooltip === 'string') {
		title = item.tooltip;
	} else if (item.ariaLabel) {
		title = item.ariaLabel;
	}

	item.disabled = disabled?.(item.key) || false;

	const getIcon = () => {
		const label = item.label || '';
		return IconComponent ? <IconComponent label={label} /> : undefined;
	};

	return {
		title,
		icon: getIcon(),
		disabled: item.disabled,
		onClick: () => {
			if (typeof item.action !== 'function') {
				throw new Error(`'action' of context toolbar item '${item.key}' is not a function`);
			}

			const targetNodeAdf: ADFEntity = nodeToJSON(node);
			extension.extensionApi && item.action(targetNodeAdf, extension.extensionApi);

			return true;
		},
	};
};

const DropdownMenuExtensionItem = ({
	item,
	editorView,
	disabled,
	node,
	extension,
	dropdownOptions,
}: {
	disabled?: (key: string) => boolean;
	dropdownOptions: DropdownMenuOptions;
	editorView: EditorView;
	extension: ExtensionProps;
	item: ExtensionToolbarButton;
	node: PMNode;
}) => {
	// Use ref to keep icon component stable across renders
	const iconRef = useRef<React.ComponentType<{ label: string }> | null>(null);
	if (!iconRef.current && item.icon) {
		const loadIcon = () => resolveExtensionIcon(item.icon);
		const IconLazy = lazy(() => resolveExtensionIcon(item.icon));
		IconLazy.displayName = 'lazy(ExtensionIcon)';
		const IconLoadable = Loadable({
			loader: loadIcon,
			loading: noop,
		});
		const Icon = (props: { label: string }) =>
			isExperimentEnabled('platform_editor_loosely_lazy_migration') ? (
				<LazySuspense fallback={null}>
					{/* eslint-disable-next-line react/jsx-props-no-spreading -- pass icon props to the lazy component */}
					<IconLazy {...props} />
				</LazySuspense>
			) : (
				// eslint-disable-next-line react/jsx-props-no-spreading -- pass icon props to the legacy component
				<IconLoadable {...props} />
			);
		Icon.preload = () =>
			isExperimentEnabled('platform_editor_loosely_lazy_migration')
				? IconLazy.preload()
				: IconLoadable.preload();
		iconRef.current = Icon;
	}

	const dropdownItem = convertExtensionToDropdownMenuItem({
		item,
		disabled,
		node,
		extension,
		IconComponent: iconRef.current,
	});

	if (!dropdownItem) {
		return null;
	}

	return (
		<DropdownMenuItem
			key={item.key}
			item={dropdownItem}
			editorView={editorView}
			hide={dropdownOptions.hide}
			dispatchCommand={dropdownOptions.dispatchCommand}
			showSelected={dropdownOptions.showSelected}
			intl={dropdownOptions.intl}
		/>
	);
};

export const DropdownMenuExtensionItems = (
	props: OverflowExtensionPlaceholderProps,
): React.JSX.Element | null => {
	const { node, editorView, extension, disabled, dropdownOptions, areAnyNewToolbarFlagsEnabled } =
		props;

	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const [extensions, setExtensions] = useState<ExtensionManifest<any>[]>([]);
	useEffect(() => {
		getExtensions();

		async function getExtensions() {
			const provider = await extension.extensionProvider;
			if (provider) {
				setExtensions(await provider.getExtensions());
			}
		}
	}, [extension.extensionProvider]);

	const nodeAdf = React.useMemo(() => nodeToJSON(node), [node]);
	const extensionItems = React.useMemo(() => {
		if (!extension.extensionApi) {
			return [];
		}
		return getContextualToolbarItemsFromModule(extensions, nodeAdf, extension.extensionApi);
	}, [extensions, nodeAdf, extension.extensionApi]);

	if (!extensionItems.length || !dropdownOptions) {
		return null;
	}

	return (
		<>
			{extensionItems.map((item, _idx) => {
				if (!('key' in item)) {
					return null;
				}
				return (
					<DropdownMenuExtensionItem
						key={item.key}
						item={item}
						editorView={editorView}
						disabled={disabled}
						node={node}
						extension={extension}
						dropdownOptions={dropdownOptions}
					/>
				);
			})}
			{areAnyNewToolbarFlagsEnabled && <DropdownSeparator />}
		</>
	);
};
